'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useState, useEffect, useRef, useCallback } from 'react';

import { useBooksCount } from '../../../hooks/useBooksCount';
import { useAuth } from '../../../hooks/useAuth';
import { useUI } from '../../../contexts/UIContext';
import { getSiteConfig } from '../../../config/sites';
import styles from '../../../styles/components/header/header.module.scss';

const site = getSiteConfig();

import LoginPopup from '../auth/LoginPopupLazy';
import SiteSwitch from './SiteSwitch';
import { NAV_ITEMS, activeNavItemIndex, isNavItemActive } from './navItems';
import { useCart } from '../../../contexts/CartContext';

/**
 * Четирите долни се рендерират САМО зад условие — профилът зад `isLoggedIn`
 * (който минава през `useSession`, тоест на първия рендер е false дори за
 * логнат потребител), останалите три зад клик. Въпреки това стояха в критичния
 * път: измерено на прод 2026-08-29, `LoginPopup` беше и в layout, и в page
 * chunk-а (внасяше се и от `Homepage.js`), заедно с `RegisterPopup`,
 * `ForgotPasswordPopup` и `userValidation` — 100% неизползван код, който се
 * състезава за честотна лента с LCP корицата.
 *
 * `ssr: false` е коректен: нито един от тях не рендерира нищо на сървъра.
 */
const ProfileMenu = dynamic(() => import('./ProfileMenu'), { ssr: false });
const SavedBooksMenu = dynamic(() => import('./SavedBooksMenu'), { ssr: false });
const CartMenu = dynamic(() => import('./CartMenu'), { ssr: false });

// Константа за максимален брой в badge
const MAX_BADGE_COUNT = 99;
const HOME_RESET_EVENT = 'travogue:home-reset';
/**
 * От тази ширина надолу навигацията минава в хамбургера. Стойността е и в
 * `$bp-burger` в header.module.scss — пази ги еднакви (има тест).
 */
const BURGER_BREAKPOINT = 600;

const Header = ({ _showCanvas, setShowCanvas }) => {
  const { isLoggedIn } = useAuth();
  const { loginOpen, setLoginOpen, runPendingAfterLogin, headerSearchOpen, setHeaderSearchOpen } =
    useUI();
  const [showSavedBooks, setShowSavedBooks] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { items: cartItems } = useCart();
  const cartAnchorRef = useRef(null);
  const pathname = usePathname();
  const router = useRouter();

  // На мобилно: индикаторът се позиционира с CSS (data-active-index).
  // На десктоп — с useEffect по-долу.
  const navMenuRef = useRef(null);
  const indicatorRef = useRef(null);
  const savedBooksAnchorRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileNavRef = useRef(null);
  const activeNavIndex = activeNavItemIndex(pathname);

  const savedBooksCount = useBooksCount();

  // Sliding indicator за десктоп и таблет (позиция/ширина от съдържанието на
  // линковете). Под мобилно менюто е скрито (хамбургер) — индикаторът се нулира.
  useEffect(() => {
    const updateIndicator = () => {
      if (typeof window !== 'undefined' && window.innerWidth <= BURGER_BREAKPOINT) {
        if (indicatorRef.current) {
          indicatorRef.current.style.left = '';
          indicatorRef.current.style.width = '';
          indicatorRef.current.style.opacity = '';
        }
        return;
      }
      if (!indicatorRef.current || !navMenuRef.current) {
        return;
      }

      const navMenu = navMenuRef.current;
      const indicator = indicatorRef.current;
      const activeItem = navMenu.querySelector(`.${styles.navItem}.${styles.active}`);
      if (!activeItem) {
        indicator.style.opacity = '0';
        return;
      }
      indicator.style.opacity = '1';
      const navRect = navMenu.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      indicator.style.left = `${itemRect.left - navRect.left}px`;
      indicator.style.width = `${itemRect.width}px`;
    };

    const t = setTimeout(updateIndicator, 10);
    window.addEventListener('resize', updateIndicator);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [pathname]);

  const handleHomeClick = useCallback(
    (e) => {
      if (pathname === '/') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(HOME_RESET_EVENT));
        return;
      }
      if (pathname === '/project' || pathname === '/checkout') {
        e.preventDefault();
        router.push('/');
      }
      // no-op (mobile menu removed)
    },
    [pathname, router],
  );

  /**
   * Всичко в хедъра се отваря върху едно и също място. Без взаимното затваряне
   * мобилното меню и количката/търсачката застават едновременно на екрана —
   * панелът на търсачката (`#header-search-slot`) дори ляга върху менюто.
   */
  const handleLoginClick = useCallback(() => {
    setShowMobileNav(false);
    setLoginOpen(true);
  }, [setLoginOpen]);

  const handleToggleSavedBooks = useCallback(() => {
    setShowMobileNav(false);
    setShowSavedBooks((prev) => !prev);
  }, []);

  const handleCloseSavedBooks = useCallback(() => {
    setShowSavedBooks(false);
  }, []);

  const handleToggleCart = useCallback(() => {
    setShowMobileNav(false);
    setShowCart((prev) => !prev);
  }, []);

  const handleCloseCart = useCallback(() => {
    setShowCart(false);
  }, []);

  const handleCloseLogin = useCallback(() => {
    setLoginOpen(false);
  }, [setLoginOpen]);

  // Затваряне на login/register попъпа при отваряне на страницата за успешна регистрация
  useEffect(() => {
    if (pathname === '/pages/register-success') {
      setLoginOpen(false);
    }
  }, [pathname, setLoginOpen]);

  const handleToggleSearch = useCallback(() => {
    setShowMobileNav(false);
    setHeaderSearchOpen((prev) => !prev);
  }, [setHeaderSearchOpen]);

  const handleToggleMobileNav = useCallback(() => {
    setHeaderSearchOpen(false);
    setShowCart(false);
    setShowSavedBooks(false);
    setShowMobileNav((prev) => !prev);
  }, [setHeaderSearchOpen]);

  const handleCloseMobileNav = useCallback(() => {
    setShowMobileNav(false);
  }, []);

  const handleMobileNavLinkClick = useCallback(
    (e) => {
      handleCloseMobileNav();
      const href = e.currentTarget.getAttribute('href') || '/';
      if (pathname === '/' && href === '/') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(HOME_RESET_EVENT));
        return;
      }
      if (pathname === '/project' || pathname === '/checkout') {
        e.preventDefault();
        router.push(href);
      }
    },
    [handleCloseMobileNav, pathname, router],
  );

  useEffect(() => {
    setShowMobileNav(false);
    setShowSavedBooks(false);
    setShowCart(false);
    setHeaderSearchOpen(false);
  }, [pathname, setHeaderSearchOpen]);

  useEffect(() => {
    document.body.style.overflow = showMobileNav ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMobileNav]);

  /**
   * Отвореното меню се затваря при Escape, а фокусът се връща на хамбургера.
   * Обявеният `aria-modal="true"` без това е обещание, което не се спазва: с
   * клавиатура фокусът остава в страницата зад воала.
   *
   * Второто правило е срещу заключен екран: над брейкпойнта панелът е
   * `display: none`, а `overflow: hidden` на body-то остава — завъртиш ли
   * телефона с отворено меню, страницата просто спира да скролва.
   */
  useEffect(() => {
    if (!showMobileNav) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowMobileNav(false);
        hamburgerRef.current?.focus();
      }
    };

    const wide = window.matchMedia(`(min-width: ${BURGER_BREAKPOINT + 1}px)`);
    const handleWide = (e) => {
      if (e.matches) {
        setShowMobileNav(false);
      }
    };
    handleWide(wide);

    document.addEventListener('keydown', handleKeyDown);
    wide.addEventListener('change', handleWide);
    // Фокусът влиза в САМИЯ панел, не в първия линк — иначе върху „начало"
    // светва пръстен от :focus-visible и редът изглежда избран, а не фокусиран.
    mobileNavRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      wide.removeEventListener('change', handleWide);
    };
  }, [showMobileNav]);

  return (
    <div
      className={`${styles.headerBlock} ${showMobileNav ? styles.headerBlockMobileNavOpen : ''}`}
    >
      <header className={styles.header} role="banner">
        {/* Логото и превключвателят са една група, но с ясна дистанция: долепени
            се цъкат по погрешка — хората кликат логото, за да се върнат в
            началото, а съседът ги изхвърля в другия сайт. */}
        <div className={styles.brandGroup}>
          <Link
            href="/"
            onClick={handleHomeClick}
            className={styles.logo}
            aria-label="Go to homepage"
          >
            <picture>
              <source media="(max-width: 767px)" srcSet={site.branding.logoMobileUrl} />
              <img
                src={site.branding.logoUrl}
                alt={site.branding.logoAlt}
                className={styles.logoImage}
                loading="eager"
                decoding="async"
              />
            </picture>
          </Link>

          <SiteSwitch />
        </div>

        <nav className={styles.navMenu} ref={navMenuRef} data-active-index={activeNavIndex}>
          <div className={styles.slidingIndicator} ref={indicatorRef} />
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              // Само „Начало" има обработчик: от редактора/чекаута връща с
              // router.push, а на самата начална страница нулира избора.
              onClick={item.href === '/' ? handleHomeClick : undefined}
              className={`${styles.navItem} ${isNavItemActive(item, pathname) ? styles.active : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className={styles.rightIcons}>
          {isLoggedIn ? (
            <ProfileMenu />
          ) : (
            <button className={styles.iconButton} onClick={handleLoginClick} aria-label="Login">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.icon}
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
          )}

          <button
            type="button"
            className={`${styles.iconButton} ${styles.iconButtonSearch} ${headerSearchOpen ? styles.active : ''}`}
            data-header-search-toggle
            onClick={handleToggleSearch}
            aria-label="Търсене"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.icon}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          {isLoggedIn && (
            <>
              <div className={styles.savedBooksContainer}>
                <button
                  ref={savedBooksAnchorRef}
                  type="button"
                  className={`${styles.iconButton} ${showSavedBooks ? styles.active : ''}`}
                  onClick={handleToggleSavedBooks}
                  aria-label="Saved Books"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={styles.icon}
                  >
                    <path
                      d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"
                      className={styles.bookIconPath}
                    />
                    <path
                      d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"
                      className={styles.bookIconPath}
                    />
                  </svg>
                  {savedBooksCount > 0 && (
                    <span className={styles.savedBooksBadge}>
                      {savedBooksCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : savedBooksCount}{' '}
                      {/* Max badge count */}
                    </span>
                  )}
                </button>
              </div>

              {showSavedBooks && (
                <SavedBooksMenu
                  anchorRef={savedBooksAnchorRef}
                  onClose={handleCloseSavedBooks}
                  setShowCanvas={setShowCanvas}
                />
              )}
            </>
          )}

          {/* Cart иконка – показва се винаги */}
          <div className={styles.cartContainer}>
            <button
              ref={cartAnchorRef}
              type="button"
              className={`${styles.iconButton} ${showCart ? styles.active : ''}`}
              onClick={handleToggleCart}
              aria-label="Количка"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.icon}
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {cartItems.length > 0 && (
                <span className={styles.cartBadge}>
                  {cartItems.length > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : cartItems.length}
                </span>
              )}
            </button>
          </div>

          {showCart && <CartMenu anchorRef={cartAnchorRef} onClose={handleCloseCart} />}

          <button
            ref={hamburgerRef}
            type="button"
            className={styles.hamburgerButton}
            onClick={handleToggleMobileNav}
            aria-label={showMobileNav ? 'Затвори меню' : 'Отвори меню'}
            aria-expanded={showMobileNav}
          >
            <span className={`${styles.hamburgerLine} ${showMobileNav ? styles.active : ''}`} />
            <span className={`${styles.hamburgerLine} ${showMobileNav ? styles.active : ''}`} />
            <span className={`${styles.hamburgerLine} ${showMobileNav ? styles.active : ''}`} />
          </button>
        </div>

        {/* Менюто пада изпод хедъра — логото и иконите остават върху същата
            повърхност и продължават да работят, воалът покрива само страницата
            под тях. */}
        {showMobileNav && (
          <div className={styles.mobileMenu} role="dialog" aria-modal="true" aria-label="Навигация">
            <button
              type="button"
              className={styles.mobileMenuBackdrop}
              onClick={handleCloseMobileNav}
              aria-label="Затвори менюто"
            />
            <nav className={styles.mobileNav} ref={mobileNavRef} tabIndex={-1}>
              <ul className={styles.mobileNavList}>
                {NAV_ITEMS.map((item) => {
                  const { href, label, Icon } = item;
                  const active = isNavItemActive(item, pathname);
                  return (
                    <li key={href}>
                      <Link
                        href={href}
                        className={`${styles.mobileNavItem} ${active ? styles.active : ''}`}
                        onClick={handleMobileNavLinkClick}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className={styles.mobileNavIcon}>
                          <Icon />
                        </span>
                        <span className={styles.mobileNavLabel}>{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Превключвателят между сайтовете — тук, а не в хедъра на телефон:
                  там е само цветно квадратче без обяснение. */}
              <div className={styles.mobileNavSwitch}>
                <span className={styles.mobileNavSwitchHint}>Премини към</span>
                <SiteSwitch expanded />
              </div>
            </nav>
          </div>
        )}

        {loginOpen && <LoginPopup onClose={handleCloseLogin} onSuccess={runPendingAfterLogin} />}
      </header>
      <div
        id="header-search-slot"
        className={styles.headerSearchSlot}
        aria-hidden={!headerSearchOpen}
      />
    </div>
  );
};

export default Header;
